// 📄 Report.js
import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Col, Card, CardHeader, CardBody, CardFooter, Label, Spinner, Badge, Row } from 'reactstrap';
import Swal from 'sweetalert2';
import 'sweetalert2/src/sweetalert2.scss';
import ReportDetails from '../features/Reports/UserReportDetails';
import reportService from 'services/report.service';

function Report() {
    const [reports, setReports] = useState([]);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState('list'); // 'list' hoặc 'detail'
    const [dataProps, setDataProps] = useState(null);

    useEffect(() => {
        loadReports(currentPage);
    }, [currentPage]);

    /**
     * 📥 Load báo cáo theo phân trang
     */
    function loadReports(page) {
        setLoading(true);
        reportService.getAllReportsByUser(page, 10).then((result) => {
            setReports(result.data);
            setTotalPages(result.totalPages);
        }).catch((error) => {
            Swal.fire('Lỗi!', 'Không thể tải báo cáo', 'error');
        }).finally(() => {
            setLoading(false);
        });
    }


    /**
     * 🔍 Tìm kiếm báo cáo
     */
    function searchSpace(event) {
        setSearch(event.target.value);
    }

    /**
     * 🛠️ Rút gọn chuỗi
     */
    function truncateText(text, maxLength) {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    }

    /**
     * 🖱️ Xem chi tiết báo cáo
     */
    function toDetailView(report) {
        setPage('detail');
        setDataProps(report);
    }

    /**
     * ⬅️ Quay lại danh sách báo cáo
     */
    function back() {
        setPage('list');
        loadReports(currentPage);
    }

    /**
     * 🕐 Format thời gian báo cáo
     */
    function formatReportTime(dateStr) {
        if (!dateStr) return '—';
        const d = new Date(dateStr);
        const now = new Date();
        const diffMs = now - d;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        const timeStr = d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
        const dateFormatted = d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });

        // Relative time for recent reports
        let relative = '';
        if (diffMins < 1) relative = 'Vừa xong';
        else if (diffMins < 60) relative = `${diffMins} phút trước`;
        else if (diffHours < 24) relative = `${diffHours} giờ trước`;
        else if (diffDays < 7) relative = `${diffDays} ngày trước`;

        return { dateFormatted, timeStr, relative };
    }

    /**
     * 📊 Status badge
     */
    function renderStatusBadge(status) {
        const statusMap = {
            'Pending': { color: '#dc3545', bg: 'rgba(220,53,69,0.1)', label: 'Chờ xử lý' },
            'InProgress': { color: '#ffc107', bg: 'rgba(255,193,7,0.1)', label: 'Đang xử lý' },
            'Resolved': { color: '#198754', bg: 'rgba(25,135,84,0.1)', label: 'Đã xử lý' },
            'Ignored': { color: '#6c757d', bg: 'rgba(108,117,125,0.1)', label: 'Bỏ qua' }
        };
        const s = statusMap[status] || { color: '#666', bg: '#f0f0f0', label: status };
        return (
            <span style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600',
                color: s.color,
                backgroundColor: s.bg
            }}>
                {s.label}
            </span>
        );
    }

    // Shared styles
    const cardStyle = {
        borderRadius: '20px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: 'none',
        overflow: 'hidden',
        margin: '0 auto'
    };

    /**
     * 📊 Hiển thị bảng báo cáo
     */
    function renderTable() {
        const filtered = reports.filter((report) => {
            return search === '' ||
                report.username?.toLowerCase().includes(search.toLowerCase()) ||
                report.toolName?.toLowerCase().includes(search.toLowerCase()) ||
                report.errorData?.toLowerCase().includes(search.toLowerCase()) ||
                report.status?.toLowerCase().includes(search.toLowerCase());
        });

        return (
            <Table style={{ marginTop: "10px", marginBottom: 0 }}>
                <thead>
                    <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                        <th style={{ border: 'none', fontSize: '14px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase' }}>Thời gian</th>
                        <th style={{ border: 'none', fontSize: '14px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase' }}>Người báo</th>
                        <th style={{ border: 'none', fontSize: '14px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase' }}>Người xử lý</th>
                        <th style={{ border: 'none', fontSize: '14px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase' }}>Phần mềm</th>
                        <th style={{ border: 'none', fontSize: '14px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase' }}>Vị trí lỗi</th>
                        <th style={{ border: 'none', fontSize: '14px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase' }}>Dữ liệu lỗi</th>
                        <th style={{ border: 'none', fontSize: '14px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase' }}>Ghi chú</th>
                        <th style={{ border: 'none', fontSize: '14px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase' }}>Trạng thái</th>
                        <th style={{ border: 'none', fontSize: '14px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase', width: '80px' }}>Xóa</th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.map((report, index) => {
                        const time = formatReportTime(report.reportedAt || report.createdAt);
                        return (
                            <tr
                                key={index}
                                onClick={() => toDetailView(report)}
                                style={{
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f5f5f5',
                                    transition: 'background-color 0.2s ease'
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <td style={{ border: 'none', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                    <div style={{ fontSize: '15px', fontWeight: '500', color: '#2c3e50' }}>
                                        {time.dateFormatted}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#8898aa' }}>
                                        {time.timeStr}
                                        {time.relative && (
                                            <span style={{ marginLeft: '6px', color: '#51cbce', fontWeight: '500' }}>
                                                • {time.relative}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle' }}>
                                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#2c3e50' }}>{report.reportBy?.username}</div>
                                    <div style={{ fontSize: '13px', color: '#8898aa' }}>{report.reportBy?.fullname}</div>
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle', fontSize: '15px', color: '#525f7f' }}>
                                    {report.handledBy ? report.handledBy.fullname : <span style={{ color: '#c8d6e5' }}>—</span>}
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle' }}>
                                    <span style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        padding: '3px 8px',
                                        borderRadius: '20px',
                                        backgroundColor: 'rgba(81,203,206,0.1)',
                                        color: '#2c3e50'
                                    }}>
                                        {report.toolName}
                                    </span>
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle', fontSize: '15px', color: '#525f7f', maxWidth: '160px' }}>
                                    {truncateText(report.stackTrace, 30)}
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle', fontSize: '15px', color: '#525f7f', maxWidth: '160px' }}>
                                    {truncateText(report.errorDataShort, 30)}
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle', fontSize: '15px', color: '#525f7f', maxWidth: '140px' }}>
                                    {truncateText(report.note, 30)}
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle' }}>
                                    {renderStatusBadge(report.status)}
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle' }}>
                                    <Button
                                        color="danger"
                                        size="sm"
                                        style={{ borderRadius: '20px', fontSize: '13px', padding: '4px 12px', whiteSpace: 'nowrap' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            Swal.fire({
                                                title: 'Xác nhận xóa báo cáo?',
                                                icon: 'warning',
                                                showCancelButton: true,
                                                confirmButtonText: 'Xóa',
                                                cancelButtonText: 'Hủy',
                                            }).then((result) => {
                                                if (result.isConfirmed) {
                                                    reportService.deleteReport(report.id).then(() => {
                                                        Swal.fire('Thành công!', 'Xóa báo cáo thành công', 'success');
                                                        loadReports(currentPage);
                                                    }).catch((error) => {
                                                        Swal.fire('Lỗi!', `Không thể xóa báo cáo <br><br> ${error}`, 'error');
                                                    });
                                                }
                                            });
                                        }}
                                    >
                                        <i className="fas fa-times" style={{ marginRight: '4px' }} /> Xóa
                                    </Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        );
    }

    return (
        <div className="content" >
            {page === 'detail' ? (
                <>
                    <Button color="info" onClick={back} style={{ borderRadius: '20px', marginBottom: '10px' }}>
                        <i className="fas fa-chevron-left" style={{ marginRight: '6px' }} /> Quay lại
                    </Button>
                    <ReportDetails data={dataProps} onBack={back} />
                </>
            ) : (
                <Card style={cardStyle}>
                    <CardHeader style={{
                        background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                        padding: '20px 24px',
                        borderBottom: 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <i className="fas fa-bell" style={{ fontSize: '32px', color: '#fff', opacity: 0.9 }}></i>
                            <div style={{ lineHeight: '1.3' }}>
                                <Label style={{ fontSize: '18px', color: '#fff', fontWeight: '700', marginBottom: '0', display: 'block' }}>
                                    Báo lỗi từ người dùng
                                </Label>
                                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                                    {reports.length > 0 ? `${reports.length} báo cáo trên trang này` : 'Đang tải...'}
                                </span>
                            </div>
                        </div>
                        <Col md="3" style={{ padding: 0, position: 'relative' }}>
                            <i className="fas fa-search" style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: 'rgba(255,255,255,0.6)',
                                fontSize: '15px',
                                zIndex: 1
                            }}></i>
                            <Input
                                style={{
                                    height: "38px",
                                    borderRadius: '20px',
                                    border: 'none',
                                    fontSize: '14px',
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    backdropFilter: 'blur(4px)',
                                    paddingLeft: '36px'
                                }}
                                value={search}
                                onChange={searchSpace}
                                placeholder="Tìm kiếm báo cáo..."
                            />
                            <style>{`.content input::placeholder { color: rgba(255,255,255,0.6) !important; }`}</style>
                        </Col>
                    </CardHeader>
                    <CardBody style={{ padding: '16px 24px', overflowY: 'auto', overflowX: 'hidden' }}>
                        {loading ? (
                            <div className="text-center py-4">
                                <Spinner color="danger" />
                            </div>
                        ) : renderTable()}
                    </CardBody>
                    <CardFooter style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px 24px',
                        borderTop: '1px solid #f0f0f0'
                    }}>
                        <Button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(currentPage - 1)}
                            style={{
                                borderRadius: '20px',
                                fontSize: '14px',
                                padding: '6px 16px'
                            }}
                        >
                            <i className="fas fa-chevron-left" style={{ marginRight: '6px' }} /> Trang trước
                        </Button>
                        <span style={{ fontSize: '14px', color: '#8898aa' }}>Trang {currentPage} / {totalPages}</span>
                        <Button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(currentPage + 1)}
                            style={{
                                borderRadius: '20px',
                                fontSize: '14px',
                                padding: '6px 16px'
                            }}
                        >
                            Trang sau <i className="fas fa-chevron-right" style={{ marginLeft: '6px' }} />
                        </Button>
                    </CardFooter>
                </Card>
            )}
        </div>
    );
}

export default Report;
